import React from 'react';
import { View, StyleSheet } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import Typography from './Typography';
import {
  getPasswordChecks,
  getPasswordStrength,
  STRENGTH_CONFIG,
  type PasswordCheck,
} from '../../utils/passwordValidation';
import { fs, s, v } from '../../utils/libs';
import { GTFonts } from '../../theme';

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

type Props = {
  password: string;
  username?: string;
  email?: string;
  hasBlurred: boolean;
};

const CheckItem = ({
  check,
  hasBlurred,
}: {
  check: PasswordCheck;
  hasBlurred: boolean;
}) => {
  const isActive = check.met;
  const showError = !isActive && hasBlurred;

  let iconName: IoniconName;
  let iconColor: string;
  let textColor: string;

  if (isActive) {
    iconName = 'checkmark-circle';
    iconColor = '#22c55e';
    textColor = '#22c55e';
  } else if (showError) {
    iconName = 'close-circle';
    iconColor = '#ef4444';
    textColor = '#ef4444';
  } else {
    iconName = 'ellipse-outline';
    iconColor = '#94a3b8';
    textColor = '#64748b';
  }

  return (
    <View style={styles.checkRow}>
      <Ionicons name={iconName} size={14} color={iconColor} />
      <Typography style={[styles.checkText, { color: textColor }]}>
        {check.label}
      </Typography>
    </View>
  );
};

const PasswordStrengthIndicator = ({
  password,
  username,
  email,
  hasBlurred,
}: Props) => {
  if (password.length === 0) return null;

  const checks = getPasswordChecks(password, username, email);
  const strength = getPasswordStrength(password);
  const config = STRENGTH_CONFIG[strength];
  const allChecksPassed = checks.every(c => c.met);

  return (
    <View style={styles.container}>
      {/* Strength bar */}
      <View style={styles.barRow}>
        <View style={styles.barTrack}>
          <View
            style={[
              styles.barFill,
              {
                backgroundColor: config.color,
                width: `${config.barFraction * 100}%`,
              },
            ]}
          />
        </View>
        {config.label ? (
          <Typography style={[styles.strengthLabel, { color: config.color }]}>
            {config.label}
          </Typography>
        ) : null}
      </View>

      {allChecksPassed ? null : (
        <View style={styles.checkList}>
          {checks.map(check => (
            <CheckItem key={check.key} check={check} hasBlurred={hasBlurred} />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: v(4),
    marginBottom: v(8),
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(8),
    marginBottom: v(6),
  },
  barTrack: {
    flex: 1,
    height: v(4),
    backgroundColor: '#e2e8f0',
    borderRadius: s(2),
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: s(2),
  },
  strengthLabel: {
    fontSize: fs(12),
    lineHeight: fs(16),
    fontFamily: GTFonts.semibold,
    minWidth: s(42),
  },
  checkList: {
    gap: v(3),
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(6),
  },
  checkText: {
    fontSize: fs(12),
    lineHeight: fs(16),
  },
});

export default PasswordStrengthIndicator;
